import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* global window */
import { setValue, parsePath } from '@truqc/content-path';
import { clone, mergeDeepLeft } from 'ramda';
import { isEqual, isPlainObject } from 'lodash';
import { behaviorExists, nativeBehaviorEvaluator } from '../../nativeBehaviors/nativeBehaviorEvaluator';
import changeActionFactory from './changeActionFactory';
import detailActionFactory from './detailActionFactory';
import { constantsRegistry } from '../../constants/tableConstantsFactory';
import behaviors from '../../../behaviors/src/index';
import { evaluateSource } from '../../../behaviors/src/lib/evaluateSource';
import logger from '../../utils/logger';
import { post } from '../ajax';
import tokenErrorActions from '../tokenErrorActionCreators';
import notificationActions from '../notificationActionCreators';
import { applyNullRemove } from '../../utils/contentTools';
import { printResults } from '../../../behaviors/src/lib/profile';
import { configBehaviorsFunc } from '../../nativeBehaviors/configBehaviors';
import { generateMergePatchesWithAtomicValues } from '../../../behaviors/src/lib/atomic';
const _require = require('@truqc/walk-tree'),
  removeNode = _require.removeNode,
  walkTree = _require.walkTree;
const cleanNode = (node, context) => {
  if (isPlainObject(node)) {
    const properties = Object.keys(node);
    properties.forEach(prop => {
      if (node[prop] === null) {
        // eslint-disable-next-line no-param-reassign
        delete node[prop];
      }
    });
    if (Object.keys(node).length === 0 && Object.keys(context).length > 0) {
      removeNode(context);
    }
  }
};
const cleanContentMutate = content => {
  walkTree(content, cleanNode);
};
const ALLOWED_DOWNLOAD_PROTOCOLS = ['http:', 'https:'];
const getSafeDownloadHref = urlString => {
  if (typeof urlString !== 'string' || !urlString.trim()) {
    return null;
  }
  try {
    const url = new URL(urlString.trim());
    return ALLOWED_DOWNLOAD_PROTOCOLS.includes(url.protocol) ? url.href : null;
  } catch (_unused) {
    return null;
  }
};
const parseDownloadFilename = (safeHref, contentDisposition) => {
  if (typeof contentDisposition === 'string') {
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        return decodeURIComponent(utf8Match[1]).replace(/[/\\]/g, '_');
      } catch (_unused2) {
        // Fall through to filename match/path fallback.
      }
    }
    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].replace(/[/\\]/g, '_');
    }
  }
  try {
    const parsedUrl = new URL(safeHref);
    const pathNameParts = parsedUrl.pathname.split('/').filter(Boolean);
    return pathNameParts[pathNameParts.length - 1] || 'download';
  } catch (_unused3) {
    return 'download';
  }
};
const triggerBlobDownload = (blob, safeHref, contentDisposition) => {
  if (typeof window === 'undefined' || !window.URL || !window.URL.createObjectURL) {
    throw new Error('Blob download is not supported in this browser.');
  }
  if (typeof document === 'undefined' || !global.document.body) {
    throw new Error('Document is not available for blob download.');
  }
  const blobUrl = window.URL.createObjectURL(blob);
  const anchor = global.document.createElement('a');
  anchor.href = blobUrl;
  anchor.setAttribute('download', parseDownloadFilename(safeHref, contentDisposition));
  anchor.style.display = 'none';
  global.document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(blobUrl);
};
const downloadUrlAsBlob = async downloadUrl => {
  if (typeof window === 'undefined' || typeof fetch !== 'function' || !downloadUrl) {
    return {
      downloaded: false,
      error: null
    };
  }
  const safeHref = getSafeDownloadHref(downloadUrl);
  if (safeHref === null) {
    return {
      downloaded: false,
      error: null
    };
  }
  try {
    var _response$headers, _response$headers$get;
    const response = await fetch(safeHref, {
      credentials: 'omit'
    });
    if (!response.ok) {
      throw new Error("Download request failed with status ".concat(response.status));
    }
    const blob = await response.blob();
    const contentDisposition = (_response$headers = response.headers) === null || _response$headers === void 0 || (_response$headers$get = _response$headers.get) === null || _response$headers$get === void 0 ? void 0 : _response$headers$get.call(_response$headers, 'content-disposition');
    triggerBlobDownload(blob, safeHref, contentDisposition);
    return {
      downloaded: true,
      error: null
    };
  } catch (error) {
    return {
      downloaded: false,
      error
    };
  }
};
const maybeDownloadFromResult = async (result, onDownloadError) => {
  if (!result) {
    return;
  }
  if (Array.isArray(result)) {
    for (let i = 0; i < result.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await maybeDownloadFromResult(result[i], onDownloadError);
    }
    return;
  }
  if (result.success === false || !result.downloadUrl) {
    return;
  }
  const _await$downloadUrlAsB = await downloadUrlAsBlob(result.downloadUrl),
    downloaded = _await$downloadUrlAsB.downloaded,
    error = _await$downloadUrlAsB.error;
  if (!downloaded && error && onDownloadError) {
    onDownloadError(error);
  }
};

/** Behavior types that can return { downloadUrl } from evaluateSource. */
const ACTION_TYPES_THAT_MAY_RETURN_DOWNLOAD_URL = ['getSignedUrlBehavior', 'getSignedUrlForPathBehavior'];

/**
 * True if this handleActions run may produce a download (so we should pre-open a window).
 * @param {Object[]} actions - Action list passed to handleActions.
 * @param {Object} [options] - Optional { mayTriggerDownload: true } from caller.
 * @returns {boolean}
 */
const actionsMayReturnDownloadUrl = function actionsMayReturnDownloadUrl(actions) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return options.mayTriggerDownload === true || Array.isArray(actions) && actions.some(a => a && a.type && ACTION_TYPES_THAT_MAY_RETURN_DOWNLOAD_URL.includes(a.type));
};
export default function changeContentActionFactory(entity, entityRoute) {
  var _this = this;
  const constants = constantsRegistry(entity);
  const changeActions = changeActionFactory(entity, entityRoute);
  const detailActions = detailActionFactory(entity, entityRoute);

  /**
   *  Get the format from the incoming data
   */
  const getRawFormat = data => {
    let formatInput = {};
    let staticContent = {};
    let formatField = 'ReportType';
    if (entity === 'issue') {
      formatField = 'issueType';
    } else if (entity === 'form') {
      formatField = 'formType';
    }
    const entityData = Array.isArray(data) ? data[0] : data;
    if (entityData[formatField]) {
      // get format from the incoming entity defintion.
      const entityType = entityData[formatField];
      formatInput = JSON.parse(entityType.formatInput || '{}');
      staticContent = JSON.parse(entityType.staticContent || '{}');
    }
    return {
      formatInput,
      staticContent
    };
  };
  const changeContent = (id, changeEvent) => ({
    type: constants.CHANGE_CONTENT,
    id,
    changeEvent
  });
  const nativeBehaviorError = (message, error) => dispatch => {
    dispatch(notificationActions.info({
      headerMessage: message,
      message: error.message,
      id: 'native_behavior_error'
    }, {
      autoClose: false
    }));
  };

  /**
   *  Evaluate the format based on the active content and the incoming formatInput
   */
  const getEvaluatedFormat = function getEvaluatedFormat(active) {
    let formatInput = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let staticContent = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    return async dispatch => {
      let format = formatInput;
      if (active !== null && active !== void 0 && active.content && Object.keys(format).length) {
        const content = active.content;

        // evaluate format based on current content
        const evaluator = nativeBehaviorEvaluator.bind(null, active, entity);
        const nativeFunctions = {
          errorHandler: err => dispatch(nativeBehaviorError("".concat(entity, "/behaviors.evaluateFormat error"), err)),
          behaviorExists,
          evaluator,
          configBehaviors: configBehaviorsFunc
        };
        const mergedContent = mergeDeepLeft(content, staticContent);
        try {
          format = await behaviors.evaluateFormat(mergedContent, format, '', nativeFunctions);
          printResults(true);
        } catch (err) {
          logger("caught behavior error ".concat(err.message));
        }
      }
      return format;
    };
  };

  /**
   * @function formatChange - this updates the active.format value in the redux store.
   * @param {string} id
   */
  const formatChange = id => async (dispatch, getState) => {
    const _getState$entity = getState()[entity],
      active = _getState$entity.active,
      activeCache = _getState$entity.activeCache;
    const updatedFormat = await dispatch(getEvaluatedFormat(active, activeCache === null || activeCache === void 0 ? void 0 : activeCache.formatInput, activeCache === null || activeCache === void 0 ? void 0 : activeCache.staticContent));
    const activeFormat = active.format;

    // Create deep copies of the objects before using isEqual can help ensure that
    // there are no hidden references or non-enumerable properties causing inaccurate comparisons
    const activeFormatCopy = activeFormat ? JSON.parse(JSON.stringify(activeFormat)) : {};
    const updatedFormatCopy = updatedFormat ? JSON.parse(JSON.stringify(updatedFormat)) : {};

    // Only publish format changes when the format changes
    if (!isEqual(updatedFormatCopy, activeFormatCopy)) {
      return dispatch({
        type: constants.FORMAT_CHANGE,
        id,
        format: Object.keys(updatedFormat).length ? updatedFormat : activeFormat
      });
    }
    return null;
  };

  /**
   * Re-evaluate format for the active record (so it picks up latest cache, etc.)
   * and dispatch the new format so the UI re-renders with fresh data.
   * Used by rerenderBehavior when a view behavior explicitly requests a re-render.
   * @param {string} id - active record id
   */
  const forceRerender = id => async (dispatch, getState) => {
    const _ref = getState()[entity] || {},
      active = _ref.active,
      activeCache = _ref.activeCache;
    if (!active || active.id !== id) {
      return null;
    }
    const updatedFormat = await dispatch(getEvaluatedFormat(active, activeCache === null || activeCache === void 0 ? void 0 : activeCache.formatInput, activeCache === null || activeCache === void 0 ? void 0 : activeCache.staticContent));
    // When formatInput is empty, getEvaluatedFormat returns {};
    // If there is no existing active.format, don't dispatch a FORMAT_CHANGE
    // that would overwrite format with undefined/null.
    const hasUpdatedFormat = !!(updatedFormat && Object.keys(updatedFormat).length);
    if (!hasUpdatedFormat && !active.format) {
      return null;
    }
    const formatToDispatch = hasUpdatedFormat ? updatedFormat : active.format;
    return dispatch({
      type: constants.FORMAT_CHANGE,
      id,
      format: formatToDispatch
    });
  };
  const handleActions = function handleActions(entityName, actions, inPath) {
    let context = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    let options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    return async (dispatch, getState) => {
      let path = inPath;
      // remove 'content' from path.
      if (path.startsWith('content/') || path.startsWith('/content/')) {
        path = path.replace('content/', '');
      }
      const state = getState();
      const active = state[entityName].active;
      const evaluator = nativeBehaviorEvaluator.bind(null, active, entityName);
      const nativeFunctions = {
        errorHandler: err => dispatch(nativeBehaviorError("".concat(entityName, "/behaviors.evaluateSource error"), err)),
        evaluator,
        configBehaviors: configBehaviorsFunc
      };
      const shouldHandleDownloads = actionsMayReturnDownloadUrl(actions, options);
      for (let i = 0; i < actions.length; i += 1) {
        const action = actions[i];
        // eslint-disable-next-line no-await-in-loop
        const result = await evaluateSource(action, active.content, path, context, nativeFunctions);
        if (shouldHandleDownloads) {
          // eslint-disable-next-line no-await-in-loop
          await maybeDownloadFromResult(result, () => dispatch(notificationActions.error({
            message: 'Unable to download file. Please try again.',
            id: 'download_file_failed'
          })));
        }
      }
      await dispatch(formatChange(active.id));
    };
  };
  const handleEvents = (id, processed, entityName, events) => async dispatch => {
    const evaluator = nativeBehaviorEvaluator.bind(null, processed, entityName);
    const nativeFunctions = {
      errorHandler: err => dispatch(nativeBehaviorError("".concat(entity, "/behaviors.evaluate error"), err)),
      evaluator,
      configBehaviors: configBehaviorsFunc
    };
    let content = processed.content;
    for (let i = 0; i < events.length; i += 1) {
      const event = events[i];
      // eslint-disable-next-line no-await-in-loop
      const evalPatch = await behaviors.evaluate(event, content, processed.format, nativeFunctions, 'web');
      if (Object.keys(evalPatch).length > 0) {
        // Locally patch the content instead of dispatching a full save
        content = mergeDeepLeft(evalPatch, content);
        // eslint-disable-next-line no-await-in-loop
        await dispatch(changeContent(id, {
          content
        }));
      }
    }
  };
  const addAddItemIdentifierKey = (data, path) => {
    if (data === null) {
      return {};
    }
    const key = parsePath(path).pop();
    if (!key) {
      return {};
    }
    return {
      addedIdentifier: key
    };
  };

  // Different components pass different information as part
  // of their context.  This mapping provides a way for us to
  // pull out the required data based on the incoming type.
  const extractContext = {
    addButton: addAddItemIdentifierKey,
    addPhotosButton: addAddItemIdentifierKey
  };

  /* our change action is asyncronous because we need to evaluate behaviors
   * and apply any of these behavior changes that result
   */
  const change = function change(id, path, data) {
    let changeModifiers = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    return async (dispatch, getState) => {
      let response = [];
      if (path.startsWith('content/') || path.startsWith('/content/')) {
        // start of complex change.
        await dispatch(changeActions.operationPending());
        const startOfPath = path.startsWith('content/') ? 'content' : '/content';
        let safeContentPath = path.replace(startOfPath, '');
        // Replace all occurrences of double slashes with single slash
        safeContentPath = safeContentPath.replace(/\/+/g, '/');
        const _changeModifiers$preA = changeModifiers.preActions,
          preActions = _changeModifiers$preA === void 0 ? [] : _changeModifiers$preA,
          _changeModifiers$post = changeModifiers.postActions,
          postActions = _changeModifiers$post === void 0 ? [] : _changeModifiers$post,
          _changeModifiers$post2 = changeModifiers.postEvents,
          postEvents = _changeModifiers$post2 === void 0 ? [] : _changeModifiers$post2,
          contentPath = changeModifiers.contentPath,
          type = changeModifiers.type,
          parentContentPath = changeModifiers.parentContentPath,
          _changeModifiers$loca = changeModifiers.localChange,
          localChange = _changeModifiers$loca === void 0 ? true : _changeModifiers$loca,
          variables = changeModifiers.variables;
        const contextFn = extractContext[type];
        let context = {};
        // The context function, if defined, adds some extra
        // information to the context for specific component types
        if (contextFn) {
          context = contextFn(data, safeContentPath);
        }

        // contextFn add additional info for certain components
        // allow localy scope variables to spread over them if they exist
        // (generally, contextFn should just be adding things like item identifiers)
        context = _objectSpread(_objectSpread({}, context), variables);
        let actionContentPath = path;
        if (contentPath !== undefined) {
          actionContentPath = parentContentPath;
        }

        // preActions
        if (preActions.length > 0) {
          await dispatch(handleActions(entity, preActions, actionContentPath, context));
        }
        let state = getState();
        const _state$entity = state[entity],
          active = _state$entity.active,
          _state$entity$activeC = _state$entity.activeCache,
          activeCache = _state$entity$activeC === void 0 ? {} : _state$entity$activeC; // issue / report / form
        const content = active.content;
        const _activeCache$staticCo = activeCache.staticContent,
          staticContent = _activeCache$staticCo === void 0 ? {} : _activeCache$staticCo,
          formatInput = activeCache.formatInput;

        // should merge content over staticContent
        let mergedContent = mergeDeepLeft(content, staticContent);
        // only change value if the field has a content path.  Otherwise
        // we may be writing remotely or doing something else with the value.
        if (localChange) {
          // Directly set the value into the new content
          mergedContent = setValue(safeContentPath, mergedContent, data);
        }

        // set transient context for value and path
        const tmpValue = clone(data);
        cleanContentMutate(tmpValue);
        context.value = tmpValue;
        context.path = safeContentPath;
        if (formatInput !== undefined) {
          const evaluator = nativeBehaviorEvaluator.bind(null, active, entity);
          const nativeFunctions = {
            errorHandler: err => dispatch(nativeBehaviorError("".concat(entity, "/behaviors.evaluate error"), err)),
            evaluator,
            configBehaviors: configBehaviorsFunc
          };
          // Run the update event with the content after pre-actions/events and the change event
          const evalPatch = await behaviors.evaluate('update', applyNullRemove(clone(mergedContent)), formatInput, nativeFunctions, 'web');

          // We recieve a patch from behaviors, apply that
          mergedContent = mergeDeepLeft(evalPatch, mergedContent);
        }
        const contentPatch = {
          content: mergedContent
        };
        let newActive = state[entity].active;
        if (localChange) {
          // Dispatch a change to the content in the redux store
          response = await dispatch(changeContent(id, contentPatch));
          const _response = response,
            changeEvent = _response.changeEvent;
          state = getState();
          // remove null values from content. if we don't, behavior-driven events and actions will not
          // evaluate as expected.
          newActive = state[entity].active;
          const noNullsContent = applyNullRemove(clone(changeEvent.content));
          newActive.content = noNullsContent;
        }

        // postEvents
        if (postEvents.length > 0) {
          response = await dispatch(handleEvents(id, newActive, entity, postEvents));
        }
        // postActions
        if (postActions.length > 0) {
          await dispatch(handleActions(entity, postActions, actionContentPath, context));
        }
        state = getState();
        const activeAlert = state[entity].activeAlert;
        if (!activeAlert || !localChange) {
          response = await dispatch(formatChange(id));
        }
      } else {
        // not a content change, so use non-mergePatch change method.
        response = await dispatch(changeActions.change(id, path, data));
      }
      await dispatch(changeActions.operationComplete());
      return response;
    };
  };

  /**
   *  Save the actions to the server, allowing client code, specifically create,
   *  to override the function called on successful save.
   */
  const serverSaveMergePatches = (id, mergePatches, onSuccessIn, handleRedirect, postOpts) => async dispatch => {
    const onSuccess = onSuccessIn || changeActions.saveSuccess;
    await dispatch(changeActions.save(id));
    try {
      const url = "/".concat(entityRoute, "/applyMergePatches");
      const response = await post(url, mergePatches, true, postOpts);
      return dispatch(onSuccess(id, response.data));
    } catch (e) {
      return dispatch(tokenErrorActions.handleError(e, entity, changeActions.saveFail.bind(this, id, e), serverSaveMergePatches.bind(this, id, mergePatches, onSuccess, handleRedirect, postOpts), handleRedirect));
    }
  };

  /**
   *  Save the actions to the server, allowing client code, specifically create,
   *  to override the function called on successful save.
   */
  const _serverSaveAllMergePatches = function serverSaveAllMergePatches(mergePatches) {
    let onSuccess = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : changeActions.saveSuccess;
    return async (dispatch, getState) => {
      const loading = getState()[entity].loading;
      if (!loading) {
        dispatch(changeActions.saveAll());
        const url = "/".concat(entityRoute, "/applyMergePatches");
        try {
          const response = await post(url, mergePatches);
          return dispatch(changeActions.saveAllSuccess(response.data));
        } catch (e) {
          return dispatch(tokenErrorActions.handleError(e, entity, changeActions.saveAllFail.bind(_this, e), _serverSaveAllMergePatches.bind(_this, mergePatches, onSuccess)));
        }
      }
      return dispatch(notificationActions.info({
        message: 'Please wait, your changes are being saved.',
        id: 'cancel_save_request'
      }));
    };
  };

  /**
   *  The content has been saved by the server and any updates are sent back in the response.
   */
  const saveSuccess = (id, response) => async (dispatch, getState) => {
    let data = response;
    if (typeof response === 'string') {
      try {
        data = JSON.parse(response);
      } catch (err) {
        // ignore
      }
    }
    if (Array.isArray(data)) {
      data = data[0];
    }

    // Re-attach the format to the entity. This used to happen with an explict dispatch of
    // formatChange, but a change in format will cause an expensive full re-render
    const _getState$entity2 = getState()[entity],
      active = _getState$entity2.active,
      activeCache = _getState$entity2.activeCache;
    let _getRawFormat = getRawFormat(data),
      formatInput = _getRawFormat.formatInput,
      staticContent = _getRawFormat.staticContent;
    if (!Object.keys(formatInput).length) {
      formatInput = activeCache === null || activeCache === void 0 ? void 0 : activeCache.formatInput;
    }
    if (!Object.keys(staticContent).length) {
      staticContent = activeCache === null || activeCache === void 0 ? void 0 : activeCache.staticContent;
    }
    data.format = await dispatch(getEvaluatedFormat(_objectSpread(_objectSpread({}, active), data), formatInput, staticContent));
    return dispatch(changeActions.saveSuccess(id, data));
  };
  const noop = () => ({
    type: constants.NOOP
  });

  /**
   *  Send a mergePatch to the server, first forming the list of actions
   *  expected by serverSaveMergePatches
   */
  const serverSave = (version, handleRedirect, postOpts) => (dispatch, getState) => {
    const currentEntity = getState()[entity];
    if (!(currentEntity && currentEntity.active && currentEntity.active.id)) {
      return dispatch(noop());
    }
    const _currentEntity$origin = currentEntity.originalActive,
      _currentEntity$origin2 = _currentEntity$origin === void 0 ? {} : _currentEntity$origin,
      _currentEntity$origin3 = _currentEntity$origin2.content,
      originalContent = _currentEntity$origin3 === void 0 ? {} : _currentEntity$origin3,
      _currentEntity$active = currentEntity.active,
      _currentEntity$active2 = _currentEntity$active === void 0 ? {} : _currentEntity$active,
      id = _currentEntity$active2.id,
      _currentEntity$active3 = _currentEntity$active2.content,
      currentContent = _currentEntity$active3 === void 0 ? {} : _currentEntity$active3,
      isNew = _currentEntity$active2.isNew,
      active = currentEntity.active,
      _currentEntity$active4 = currentEntity.activeCache,
      _currentEntity$active5 = _currentEntity$active4 === void 0 ? {} : _currentEntity$active4,
      _currentEntity$active6 = _currentEntity$active5.formatInput,
      formatInput = _currentEntity$active6 === void 0 ? {} : _currentEntity$active6;
    try {
      const generated = generateMergePatchesWithAtomicValues(formatInput, originalContent, currentContent);
      if (generated === undefined) {
        // even if no changes need to be saved to server,
        // we need to reset
        // redux to a success state.
        return dispatch(saveSuccess(id, active));
      }
      const _generated = _slicedToArray(generated, 2),
        preMergePatch = _generated[0],
        mergePatch = _generated[1];
      if (!mergePatch.version && version) {
        mergePatch.version = version;
      }
      const patches = [];
      if (Object.keys(mergePatch).length > 0) {
        if (Object.keys(preMergePatch).length > 0 && !isNew) {
          // only push valid preMergePatches and
          // if the entity is not new.  New entities
          // don't need to null pre-existing data,
          // and we need to send the creation patch
          // first.
          patches.push({
            id,
            patch: preMergePatch
          });
        }
        patches.push({
          id,
          patch: mergePatch
        });
        return dispatch(serverSaveMergePatches(id, patches, saveSuccess, handleRedirect, postOpts), getState);
      }
      return dispatch(noop());
    } catch (e) {
      return dispatch(tokenErrorActions.handleError(e, entity, changeActions.saveFail.bind(this, id, e), serverSave.bind(this, version, handleRedirect, postOpts), handleRedirect));
    }
  };

  /**
   *  Batch set multiple values in the editor cache.
   *  This prevents multiple re-renders and expensive deep copies.
   */
  const setEditorCacheValues = updates => ({
    type: constants.SET_EDITOR_CACHE_VALUES,
    updates
  });

  /**
   * Extract cache updates from preloaded query results (queryFormBehavior or queryEntityBehavior).
   * Validates that results exist, have keys, and that each result is an array.
   *
   * @param {Object} preloadedResults - Object keyed by cache key with array values
   * @returns {Array} Array of cache update objects { name, value }
   */
  const extractQueryResultCacheUpdates = preloadedResults => {
    if (!preloadedResults || Object.keys(preloadedResults).length === 0) {
      return [];
    }
    const cacheUpdates = [];
    Object.keys(preloadedResults).forEach(cacheKey => {
      const queryResult = preloadedResults[cacheKey];
      if (Array.isArray(queryResult)) {
        cacheUpdates.push({
          name: cacheKey,
          value: queryResult
        });
      }
    });
    return cacheUpdates;
  };

  /**
   *  Prepare format and update the record with the view behavior changes
   */
  const showSuccess = (id, data, query) => async dispatch => {
    const _getRawFormat2 = getRawFormat(data),
      formatInput = _getRawFormat2.formatInput,
      staticContent = _getRawFormat2.staticContent;

    // Store preloaded forms in cache BEFORE format evaluation
    // This ensures the cache is populated before evaluateFormat triggers remote loading
    const allCacheUpdates = [];
    if (data.preloadedForms) {
      Object.keys(data.preloadedForms).forEach(formID => {
        const preloadedForm = data.preloadedForms[formID];
        if (preloadedForm !== null && preloadedForm !== void 0 && preloadedForm.content) {
          // Use formID as-is from database to maintain case consistency
          // Ensure content has id property (getFormContent checks content.id)
          const formContent = _objectSpread(_objectSpread({}, preloadedForm.content), {}, {
            id: preloadedForm.id || formID
          });
          allCacheUpdates.push({
            name: "formCache/".concat(formID),
            value: formContent
          });
        }
      });
    }

    // Store preloaded queryFormBehavior results in cache BEFORE format evaluation
    // This ensures queryFormBehavior cache hits occur before making server requests
    allCacheUpdates.push(...extractQueryResultCacheUpdates(data.preloadedQueryResults));

    // Store preloaded queryEntityBehavior results in cache BEFORE format evaluation
    // This ensures queryEntityBehavior cache hits occur before making server requests
    allCacheUpdates.push(...extractQueryResultCacheUpdates(data.preloadedQueryEntityResults));

    // Batch dispatch all cache updates in a single action to avoid multiple re-renders
    // and expensive JSON.parse(JSON.stringify()) operations
    if (allCacheUpdates.length > 0) {
      dispatch(setEditorCacheValues(allCacheUpdates));
    }

    // run view behaviors before format evaluation
    const evaluator = nativeBehaviorEvaluator.bind(null, data, entity);
    const nativeFunctions = {
      errorHandler: err => dispatch(nativeBehaviorError("".concat(entity, "/behaviors.evaluateSource error"), err)),
      evaluator,
      configBehaviors: configBehaviorsFunc
    };

    // evaluate format and dispatch changes
    const format = await dispatch(getEvaluatedFormat(data, formatInput, staticContent));

    // dispatch the active record
    const active = _objectSpread(_objectSpread({}, data), {}, {
      // note that this is content without view behaviors changes
      format
    });
    await dispatch(detailActions.showSuccess(id, active, query));

    // View behaviors depend on an `active` record, e.g. writeContentBehavior.
    // Run behaviors after the active dispatch above. View behaviors that need the UI to
    // re-render with fresh format (e.g. after changing cache) should use rerenderBehavior.
    const evalPatchView = await behaviors.evaluate('view', data.content, formatInput, nativeFunctions, 'web');
    if (Object.keys(evalPatchView).length > 0) {
      const updatedContent = mergeDeepLeft(evalPatchView, data.content);
      // Update the record with view behavior changes and mark the record dirty
      // This has to be done after the showSuccess call, which clears out pendingChanges
      await dispatch(changeContent(id, {
        content: updatedContent
      }));
    }
  };
  const serverShow = function serverShow(id) {
    let query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let successFunc = arguments.length > 2 ? arguments[2] : undefined;
    let failureFunc = arguments.length > 3 ? arguments[3] : undefined;
    let handleRedirect = arguments.length > 4 ? arguments[4] : undefined;
    return detailActions.serverShow(id, query, successFunc || showSuccess, failureFunc, handleRedirect);
  };

  /**
   *  Set a value in the editor cache
   */
  const setEditorCacheValue = (name, value) => ({
    type: constants.SET_EDITOR_CACHE_VALUE,
    name,
    value
  });

  // return all our actions.
  return _objectSpread(_objectSpread(_objectSpread({}, changeActions), detailActions), {}, {
    change,
    changeContent,
    formatChange,
    forceRerender,
    getEntityRoute: () => entityRoute,
    // exposing the import here avoids circular dependencies
    getNativeBehaviorEvaluator: () => nativeBehaviorEvaluator,
    handleActions,
    nativeBehaviorError,
    saveSuccess,
    serverSave,
    serverSaveMergePatches,
    serverSaveAllMergePatches: _serverSaveAllMergePatches,
    serverShow,
    showSuccess,
    setEditorCacheValue,
    setEditorCacheValues
  });
}